import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JournalEntry, MapPin, PageState, ServerMessage } from '@roll1/shared';
import { beatMapIds, beatPrimaryMapId } from '@roll1/shared';
import { toast } from 'sonner';
import type { StudioMapLink } from '../../contexts/StudioMapLinkContext.js';
import type { TranslationKeys } from '../../i18n/types.js';
import type { StudioEntityRef } from './StudioRail.js';
import type { Beat } from './StudioRail.js';
import { beatDisplayName } from './ownerPlace.js';

/**
 * P09b (Q-7 / Q-8) — what ties a note to its pin and to the map beside the beat, in one place:
 *  · the pin each note has (kept live from the pin echoes: a map held by a pane keeps its pins in its own hooks, not in the store);
 *  · «Place on map»: the next click on the chosen map makes the pin — GM only at first;
 *  · a note card dropped on a map: a pin — or a question first when the map is not the note's beat's;
 *  · a pin clicked on a map: the note of the open beat is scrolled to and lit (the beat is not replaced);
 *  · the pin lit from its note (hover) or from its badge (press).
 */

type Translate = (key: keyof TranslationKeys) => string;

interface MpLike {
  readonly session?: { readonly id: string } | null;
  send(msg: never): unknown;
  onMessage(handler: (msg: ServerMessage) => void): () => void;
}

interface PagesLike {
  readonly pages: readonly { readonly id: string; readonly name?: string }[];
  readonly hydrationSeq?: number;
  getPageData?: (id: string) => PageState | null;
}

export interface NoteAsk {
  readonly entryId: string;
  readonly noteName: string;
  readonly place: string;
  readonly pageId: string;
  readonly x: number;
  readonly y: number;
}

export interface UseStudioNoteMapArgs {
  readonly mp: MpLike;
  readonly pagesApi: PagesLike;
  readonly entries: readonly JournalEntry[];
  readonly beats: readonly Beat[];
  readonly selectedBeatId: string | undefined;
  /** The workspace now: nothing in the main pane = the beat's overview. */
  readonly focusRef: StudioEntityRef | undefined;
  readonly sideRef: StudioEntityRef | undefined;
  readonly t: Translate;
  /** Keep a map in the second pane. */
  readonly openMapBeside: (pageId: string) => void;
  /** The temporary view of an object the map pointed at. */
  readonly openFromMap: (ref: StudioEntityRef, sourcePageId: string) => void;
  readonly shareEntry: (entryId: string, name: string) => void;
}

export interface NotePin {
  readonly pin: MapPin;
  readonly pageName: string;
}

const HIGHLIGHT_NOTE_MS = 4000;
const FLASH_PIN_MS = 3000;
const PIN_COLOR = '#ef4444';

export function useStudioNoteMap(args: UseStudioNoteMapArgs) {
  const { mp, pagesApi, entries, beats, selectedBeatId, focusRef, sideRef, t, openMapBeside, openFromMap, shareEntry } = args;
  const latest = useRef(args);
  latest.current = args;

  // ── the pin of each note, live ──
  const pinsRef = useRef(new Map<string, MapPin>());
  const [pinTick, setPinTick] = useState(0);
  useEffect(() => {
    const all = new Map<string, MapPin>();
    for (const pg of pagesApi.pages) {
      for (const pin of pagesApi.getPageData?.(pg.id)?.pins ?? []) all.set(pin.id, pin);
    }
    pinsRef.current = all;
    setPinTick((n) => n + 1);
    // Reseeded by every join snapshot (the store was rebuilt).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesApi.hydrationSeq]);
  useEffect(() => mp.onMessage((msg: ServerMessage) => {
    switch (msg.type) {
      case 'pin:created': pinsRef.current.set(msg.pin.id, msg.pin); break;
      case 'pin:updated': {
        const cur = pinsRef.current.get(msg.pinId);
        if (cur !== undefined) pinsRef.current.set(msg.pinId, { ...cur, ...msg.changes } as MapPin);
        break;
      }
      case 'pin:deleted': pinsRef.current.delete(msg.pinId); break;
      default: return;
    }
    setPinTick((n) => n + 1);
  }), [mp]);

  const pinsByEntry = useMemo((): ReadonlyMap<string, NotePin> => {
    const out = new Map<string, NotePin>();
    for (const pin of pinsRef.current.values()) {
      if (pin.journalEntryId === null || out.has(pin.journalEntryId)) continue;
      const pageName = pagesApi.pages.find((p) => p.id === pin.pageId)?.name ?? pin.pageId;
      out.set(pin.journalEntryId, { pin, pageName });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinTick, pagesApi.pages]);

  // ── lit pin / lit note ──
  const [hoverPinId, setHoverPinId] = useState<string | undefined>(undefined);
  const [flashPinId, setFlashPinId] = useState<string | undefined>(undefined);
  const flashTimer = useRef<number | undefined>(undefined);
  const [highlightedNoteId, setHighlightedNoteId] = useState<string | undefined>(undefined);
  const noteTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => {
    window.clearTimeout(flashTimer.current);
    window.clearTimeout(noteTimer.current);
  }, []);

  const hoverNote = useCallback((entryId: string | null): void => {
    setHoverPinId(entryId === null ? undefined : pinsByEntry.get(entryId)?.pin.id);
  }, [pinsByEntry]);

  const flashPin = useCallback((entryId: string): void => {
    const np = pinsByEntry.get(entryId);
    if (np === undefined) return;
    const visible = [sideRef, focusRef].some((r) => r?.kind === 'page' && r.id === np.pin.pageId);
    if (!visible) {
      toast(t('studio.note.pinOffscreen').replace('{map}', np.pageName));
      return;
    }
    setFlashPinId(np.pin.id);
    window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashPinId(undefined), FLASH_PIN_MS);
  }, [pinsByEntry, sideRef, focusRef, t]);

  // ── which map a note belongs on ──
  const beatOfNote = useCallback((entry: JournalEntry): Beat | undefined => {
    const owner = entry.owner;
    if (owner?.kind === 'beat') return beats.find((b) => b.id === owner.beatId);
    if (owner?.kind === 'objective' || owner?.kind === 'quest') {
      return beats.find((b) => ((b as { materials?: { kind: string; id: string }[] }).materials ?? []).some((m) => m.kind === 'journal' && m.id === owner.questId));
    }
    return undefined;
  }, [beats]);

  const mapOfNote = useCallback((entry: JournalEntry): string | undefined => {
    const beat = beatOfNote(entry) ?? beats.find((b) => b.id === selectedBeatId);
    if (beat === undefined) return undefined;
    const shape = beat as { pageId?: string | null; pageIds?: string[] };
    return beatPrimaryMapId(shape) ?? beatMapIds(shape)[0];
  }, [beatOfNote, beats, selectedBeatId]);

  // ── «Place on map» ──
  const [placing, setPlacing] = useState<{ entryId: string; pageId: string } | undefined>(undefined);
  useEffect(() => {
    if (placing === undefined) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setPlacing(undefined); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [placing]);

  // The map was closed (or another one put beside) while a place was being chosen: the mode ends with it.
  useEffect(() => {
    if (placing !== undefined && !(sideRef?.kind === 'page' && sideRef.id === placing.pageId)) setPlacing(undefined);
  }, [placing, sideRef]);

  const createPin = useCallback((entryId: string, pageId: string, x: number, y: number): void => {
    const { mp: m, entries: es, pagesApi: pg, t: tr } = latest.current;
    if (!m.session) return;
    m.send({ type: 'pin:create', pageId, x, y, icon: 'pin', color: PIN_COLOR, visibility: 'gm', journalEntryId: entryId } as never);
    const name = es.find((e) => e.id === entryId)?.name ?? '';
    const map = pg.pages.find((p) => p.id === pageId)?.name ?? pageId;
    toast(tr('studio.note.pinPlaced').replace('{name}', name).replace('{map}', map));
  }, []);

  const placeOnMap = useCallback((entryId: string): void => {
    const entry = entries.find((e) => e.id === entryId);
    const pageId = entry === undefined ? undefined : mapOfNote(entry);
    if (pageId === undefined) {
      toast.error(t('studio.note.noMapForPlace'));
      return;
    }
    openMapBeside(pageId);
    setPlacing({ entryId, pageId });
  }, [entries, mapOfNote, openMapBeside, t]);

  const placeAt = useCallback((pageId: string, x: number, y: number): void => {
    const cur = placing;
    if (cur === undefined || cur.pageId !== pageId) return;
    setPlacing(undefined);
    createPin(cur.entryId, pageId, x, y);
  }, [placing, createPin]);

  // ── a note card dropped on a map ──
  const [ask, setAsk] = useState<NoteAsk | undefined>(undefined);
  const dropNote = useCallback((entryId: string, pageId: string, x: number, y: number): void => {
    const { entries: es, beats: bs, t: tr } = latest.current;
    const entry = es.find((e) => e.id === entryId);
    if (entry === undefined) return;
    const beat = beatOfNote(entry);
    const owner = entry.owner;
    // A shared note lives nowhere in particular: any map will do. The beat's own maps need no question.
    if (beat === undefined || owner === undefined || owner.kind === 'shared') {
      createPin(entryId, pageId, x, y);
      return;
    }
    const maps = beatMapIds(beat as { pageId?: string | null; pageIds?: string[] });
    if (maps.includes(pageId)) {
      createPin(entryId, pageId, x, y);
      return;
    }
    const place = owner.kind === 'objective'
      ? tr('studio.noteDrop.inObjective')
          .replace('{note}', entry.name)
          .replace('{objective}', (() => {
            const quest = es.find((e) => e.id === owner.questId);
            const objectives = ((quest?.data as { objectives?: { id: string; text: string }[] } | undefined)?.objectives ?? []);
            return objectives.find((o) => o.id === owner.objectiveId)?.text ?? '';
          })())
      : tr('studio.noteDrop.inBeat').replace('{note}', entry.name).replace('{beat}', beatDisplayName(bs.find((b) => b.id === beat.id) ?? beat, tr));
    setAsk({ entryId, noteName: entry.name, place, pageId, x, y });
  }, [beatOfNote, createPin]);

  const answerAsk = useCallback((choice: 'share' | 'keep' | 'cancel'): void => {
    const cur = ask;
    setAsk(undefined);
    if (cur === undefined || choice === 'cancel') return;
    if (choice === 'share') shareEntry(cur.entryId, cur.noteName);
    createPin(cur.entryId, cur.pageId, cur.x, cur.y);
  }, [ask, createPin, shareEntry]);

  // ── a pin clicked on a map ──
  const pinClick = useCallback((pin: MapPin, pageId: string): void => {
    const { entries: es, beats: bs, selectedBeatId: sel, focusRef: focus } = latest.current;
    if (pin.journalEntryId === null) return;
    const entry = es.find((e) => e.id === pin.journalEntryId);
    if (entry === undefined) return;
    const beat = sel === undefined ? undefined : bs.find((b) => b.id === sel);
    const owner = entry.owner;
    const ofOpenBeat = beat !== undefined && (
      (owner?.kind === 'beat' && owner.beatId === beat.id)
      || (owner?.kind === 'objective' && ((beat as { materials?: { kind: string; id: string }[] }).materials ?? []).some((m) => m.kind === 'journal' && m.id === owner.questId))
    );
    // The beat stays open: its note is the answer. Another note is opened beside, as before (rule 5.4).
    if (ofOpenBeat && focus === undefined) {
      setHighlightedNoteId(entry.id);
      window.clearTimeout(noteTimer.current);
      noteTimer.current = window.setTimeout(() => setHighlightedNoteId(undefined), HIGHLIGHT_NOTE_MS);
      return;
    }
    openFromMap({ kind: 'journal', id: entry.id }, pageId);
  }, [openFromMap]);

  const highlightPinId = hoverPinId ?? flashPinId;

  const link = useMemo<StudioMapLink>(() => ({
    placing,
    onPlaceAt: placeAt,
    onDropNote: dropNote,
    onPinClick: pinClick,
    highlightPinId,
  }), [placing, placeAt, dropNote, pinClick, highlightPinId]);

  return {
    link,
    pinsByEntry,
    placing,
    cancelPlacing: (): void => setPlacing(undefined),
    placeOnMap,
    hoverNote,
    flashPin,
    highlightedNoteId,
    ask,
    answerAsk,
  };
}
